import AddCircleIcon from '@mui/icons-material/AddCircle';
import { FormBlock } from '@qbe/ads-blocks-forms';
import { FormGroupLabel, HighlightContainer, Spacer, Typography } from '@qbe/ads-core';
import React, { useState } from 'react';

import InterestedParties from '~/components/InterestedParties';
import NeedInterestedParty from '~/components/NeedInterestedParty';
import UpsertInterestedParty from '~/components/UpsertInterestedParty';
import LinkButton from '~/components-ds/LinkButton';
import { INTERESTED_PARTIES_PER_COVER_LIMIT } from '~/constants/domain-specific';
import { useAppContent, useAppDispatch, useExtraInfo, useSelectCover } from '~/hooks';
import { useAppNavigate } from '~/hooks/useAppNavigate';
import { coverDetailsMap } from '~/rules/coverDetails';
import { qalFormStartAction } from '~/thunks/analytics';
import { interestedPartyUtils } from '~/utils';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';
import { linguisticJoin } from '~/utils/linguisticJoin';

import { ContextualHelpWithButton } from '../../components-ds/ContextualHelp/ContextualHelpWithButton';

const ExtraInfoInterestedParties = () => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();

  const { completedCovers } = useSelectCover();
  const { interestedParties, needInterestedParties } = useExtraInfo();

  const coversWithoutInterestedParties = completedCovers.filter(
    (c) => !coverDetailsMap[c].canHaveInterestedParties
  );
  const coversWithInterestedParties = completedCovers.filter(
    (c) => coverDetailsMap[c].canHaveInterestedParties
  );

  const industryPartiesExist = interestedParties?.length !== 0;
  const maxPartyLimit =
    interestedParties?.length ===
    completedCovers.filter(interestedPartyUtils.coverNotLiability).length *
      INTERESTED_PARTIES_PER_COVER_LIMIT;

  const [addAnotherParty, setAddAnotherParty] = useState(!industryPartiesExist);
  const [addAnotherPartyButton, setAddAnotherPartyButton] = useState(industryPartiesExist);

  const onClose = () => {
    setAddAnotherParty(false);
    setAddAnotherPartyButton(true);
  };

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  const onAddAnotherParty = () => {
    setAddAnotherParty(true);
    setAddAnotherPartyButton(false);
    navigate('#add-party');
    handleFormStart();
  };

  const coversWithoutInterestedPartiesQuoted = coversWithoutInterestedParties.map((c) => `'${c}'`);

  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.extraInfoMoreBusinessDetailsLandlord',
    state: {
      coversWithoutInterestedParties_and: linguisticJoin(
        coversWithoutInterestedPartiesQuoted,
        'and'
      ),
      coversWithoutInterestedParties_or: linguisticJoin(coversWithoutInterestedPartiesQuoted, 'or'),
    },

    tokens: {
      coversWithoutInterestedParties_and: (state) => state.coversWithoutInterestedParties_and,
      coversWithoutInterestedParties_or: (state) => state.coversWithoutInterestedParties_or,
    },
  });

  return (
    <>
      {coversWithInterestedParties.length === 0 ? (
        <FormGroupLabel
          title={getTextContent('content.noInterestedParties.title')}
          subtitle={getTextContent('content.noInterestedParties.caption')}
          alignCenter
          titleSuffix={
            <ContextualHelpWithButton tracking="help: Do you need to add an interested third party?">
              <Typography>
                {getJSXContent('contextualhelpers.noInterestedParties.description')}
              </Typography>
            </ContextualHelpWithButton>
          }
        />
      ) : (
        <>
          <NeedInterestedParty
            onSelect={() => navigate(maxPartyLimit ? '#review-and-pay' : '#add-party')}
          />
          {needInterestedParties && (
            <FormBlock preset="highlight_container" paddingTop="none">
              <HighlightContainer>
                <Typography textAlign="center">
                  {getTextContent('content.needInterestedParties.caption')}
                </Typography>{' '}
              </HighlightContainer>
            </FormBlock>
          )}
        </>
      )}

      {needInterestedParties && (
        <>
          <InterestedParties />
          {industryPartiesExist && addAnotherPartyButton && (
            <>
              {!maxPartyLimit && (
                <LinkButton
                  id="add-party"
                  label={getTextContent('buttons.addAnotherParty')}
                  dataTrack="add another party"
                  onClick={onAddAnotherParty}
                  muiIcon={AddCircleIcon}
                />
              )}
              <Spacer direction="column" size="lg" />
            </>
          )}
          {(!industryPartiesExist || addAnotherParty) && (
            <UpsertInterestedParty
              formId="add-party"
              required={!industryPartiesExist}
              onAddParty={onClose}
              onClose={onClose}
            />
          )}

          <FormBlock preset="highlight_container">
            <HighlightContainer>
              {getJSXContent('content.contactAfterPurchase.description')}
            </HighlightContainer>
          </FormBlock>
        </>
      )}
      <Spacer size="lg" />
    </>
  );
};

export default ExtraInfoInterestedParties;
